/**
 * ChatMessagesRegion — the messages slab of the chat layout. The page is
 * window-scrolled (review fix plan 4, Finding 2): this component flows the
 * `ChatMessagesList` in the document (no local scroll container) and renders
 * every overlay in a SIBLING `position: fixed` viewport layer spanning the gap
 * between the sticky app bar (top inset) and the sticky composer (bottom
 * inset). The list grows with content, so overlays can no longer be `absolute`
 * against it — `inset: 0` would cover the whole scroll height and `bottom: X`
 * would land at the bottom of the *content*, not the viewport. Pinning them to
 * the fixed inter-bar band keeps every overlay glued to the visible strip while
 * the document scrolls underneath.
 *
 * Overlays: subscribe CTA, empty state, blocked overlay, unread banner,
 * send-error banner, first-load indicator, pagination-loading indicator.
 * Mirrors Flutter's `Stack` slot in `chat_screen.dart:960-984`. Render-only;
 * state lives in `useChat` in the parent. Z-order within the fixed layer:
 * subscribe CTA (2) → empty state (1, covered by blocked) → blocked (3) →
 * unread banner (4) → send-error banner (5) → first-load (6) → pagination
 * loader (7).
 *
 * Deviation from Flutter UI: Flutter renders the pagination loader as the
 * last item of the reversed `ListView` so it scrolls with the list and lands
 * at the top of the visible viewport (`chat_screen.dart:3875-3884`). The web
 * port intentionally renders the loader as a non-scrolling absolute overlay
 * pinned to the top of the messages region (`pointerEvents: 'none'`) so it
 * does NOT interfere with the `VirtualList(reversed)` scroll-anchor /
 * load-more-sentinel math (see plan item 11 — adding it as a top sibling
 * inside the scroll container would couple the bottom-pin math to the
 * loader's height, and inserting it as a virtualized item would shift the
 * sentinel's intersection geometry). Loader visibility is gated on
 * `isFetchingMore` so it surfaces only during an in-flight page fetch.
 */
import { useCallback, useEffect, useRef, type CSSProperties } from 'react';

import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { BlockedOverlayVariant } from '@/domain/enums/chat/blockedOverlayVariant';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';

import { SubscribeCTA } from '@/presentation/components/common/SubscribeCTA';

import { ChatMessagesList, type ChatMessagesListHandle } from './ChatMessagesList';
import { BlockedOverlays } from './BlockedOverlays';
import { UnreadBanner } from './UnreadBanner';
import { SendErrorBanner } from './SendErrorBanner';

interface ChatMessagesRegionProps {
  /**
   * Optional `data-qa-*` set forwarded to the virtualized messages list root
   * (`VirtualList`'s `qa`) so the qa-tester can scope to the message region.
   * Individual bubble rows carry their own ids (see `MessageBubble`).
   */
  qa?: QaAttrOptions;
  messages: ChatMessageEntity[];
  peerUser: UserEntity | null;
  currentUserId: string;
  openTimestampForMessageId: Record<number, boolean>;
  onToggleTimestamp: (message: ChatMessageEntity) => void;
  onRetry: (message: ChatMessageEntity) => void;
  /**
   * Page callback — join an in-progress video call as the audience. Fired only
   * by LIVE peer `videoCall` bubbles (Flutter `chat_screen.dart:2899-2924`).
   * Threaded straight down to the bubble via `ChatMessagesList`.
   */
  onVideoCallJoin?: (message: ChatMessageEntity) => void;
  onLoadMore: () => void;
  onScrolledToBottom: () => void;
  onScrolledAway: () => void;
  /**
   * Memoized index (in the newest-first `messages` array) of the most
   * recent own message marked `seenByPeer`, or -1 if none. Forwarded so the
   * dispatcher does not recompute this O(n) walk per render. See
   * `useChat.myLastMessageReadPosition`.
   */
  myLastReadPosition: number;
  screenKey: string;
  isSubscribedMutual: boolean;
  peerName: string;
  onSubscribePress: () => void;
  showEmptyState: boolean;
  peerFirstName: string;
  blockedVariant: BlockedOverlayVariant | null;
  unreadPreview: string | null;
  unreadBannerVisible: boolean;
  /** Unread-message count — forwarded to `UnreadBanner` as a `data-qa-value`. */
  unreadCount?: number;
  isScrolledUp: boolean;
  sendError: boolean;
  isOnline: boolean;
  onSendErrorDismiss: () => void;
  isFirstLoad: boolean;
  /**
   * True while a pagination page is in-flight (mirrors Flutter `_isFetching`
   * at `chat_screen.dart:97`). Drives the top-of-list pagination loader so
   * the user sees feedback while older messages are being fetched.
   */
  isFetchingMore: boolean;
  /**
   * Monotonic token from `useChat`; ticks on every successful optimistic
   * insert in `sendText`. We watch this with a `useEffect` and imperatively
   * snap the `VirtualList(reversed)` viewport to the bottom on change so the
   * just-sent bubble lands visible. Mirrors Flutter
   * `_listScrollController.animateTo(0.0, …)` at `chat_screen.dart:670-684`.
   * Tied to `sendText` only — incoming peer / own-echo messages rely on
   * `VirtualList`'s built-in at-bottom anchor and the unread-banner CTA when
   * scrolled away.
   */
  scrollToBottomToken: number;
  /**
   * Measured height (px) of the sticky top chrome (app bar + no-internet
   * banner when visible). Positions the `top` edge of the fixed overlay layer
   * and feeds the `top` half of the window bottom-pin inset.
   */
  topInset: number;
  /**
   * Measured height (px) of the sticky composer (0 when it self-hides).
   * Positions the `bottom` edge of the fixed overlay layer and feeds the
   * `bottom` half of the window bottom-pin inset.
   */
  bottomInset: number;
  /**
   * On-screen-keyboard inset (px) from `useKeyboardInset` (review fix plan 4,
   * Finding 4). The sticky composer is raised by this amount above the iOS
   * software keyboard, so the fixed overlay layer's bottom and the window
   * bottom-pin inset must add it too — otherwise overlays and a short pinned
   * list would sit behind the keyboard. `0` on desktop / when no keyboard is
   * shown.
   */
  keyboardInset: number;
}

export const ChatMessagesRegion = ({
  qa,
  messages,
  peerUser,
  currentUserId,
  openTimestampForMessageId,
  onToggleTimestamp,
  onRetry,
  onVideoCallJoin,
  onLoadMore,
  onScrolledToBottom,
  onScrolledAway,
  myLastReadPosition,
  screenKey,
  isSubscribedMutual,
  peerName,
  onSubscribePress,
  showEmptyState,
  peerFirstName,
  blockedVariant,
  unreadPreview,
  unreadBannerVisible,
  unreadCount,
  isScrolledUp,
  sendError,
  isOnline,
  onSendErrorDismiss,
  isFirstLoad,
  isFetchingMore,
  scrollToBottomToken,
  topInset,
  bottomInset,
  keyboardInset,
}: ChatMessagesRegionProps) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // Total chrome obscuring the bottom of the window viewport: the composer
  // plus, while the iOS keyboard is open, the keyboard region the composer is
  // raised above (review fix plan 4, Finding 4). Used both for the fixed
  // overlay layer's `bottom` and for the window bottom-pin inset so a short
  // list pins just above the raised composer with no gap behind the keyboard.
  const bottomChromeInset = bottomInset + keyboardInset;

  // Imperative handle into `ChatMessagesList` — used to drive the
  // `VirtualList(reversed)` viewport in response to user actions that should
  // override the scroll-anchor logic (the unread-banner / scroll-to-bottom
  // button). Mirrors Flutter's `_scrollDown` callback
  // (`chat_screen.dart:3414`, `:3452`).
  const messagesListRef = useRef<ChatMessagesListHandle>(null);

  // Tapping the unread banner / `ScrolledUpButton` must do TWO things:
  //   1. Imperatively scroll the list to the bottom (the new viewport
  //      anchor — `VirtualList.scrollToBottom()` overrides the "leave
  //      alone" decision the anchor effect would make when the user has
  //      scrolled up).
  //   2. Invoke the hook's `onScrolledToBottom` so the unread bucket
  //      flushes and the conversation is marked-seen (Flutter parity at
  //      `chat_screen.dart:547-579`).
  // Note: `VirtualList` will also fire its own `onScrolledToBottom` on the
  // scroll-edge transition once the imperative snap lands, so the hook
  // callback would run anyway — but we invoke it eagerly here so the
  // mark-seen side-effects do NOT depend on the at-bottom transition timing.
  const handleScrollDown = useCallback(() => {
    messagesListRef.current?.scrollToBottom();
    onScrolledToBottom();
  }, [onScrolledToBottom]);

  // Post-send auto-scroll. `useChat.scrollToBottomToken` ticks on every
  // successful optimistic insert in `sendText`; on change we snap the
  // `VirtualList(reversed)` viewport to the bottom so the just-sent bubble
  // lands visible — Flutter `_listScrollController.animateTo(0.0, …)` at
  // `chat_screen.dart:670-684`. We defer the imperative call by one
  // `requestAnimationFrame` so the new message has been committed to the
  // DOM (the snap reads `scrollHeight` and would otherwise miss the new
  // row). The first-render token=0 firing is skipped via a ref so we do
  // not redundantly drive the snap during the VirtualList's own first-
  // mount bottom-anchor lifecycle.
  const hasSeenInitialTokenRef = useRef<boolean>(false);
  useEffect(() => {
    if (!hasSeenInitialTokenRef.current) {
      hasSeenInitialTokenRef.current = true;
      return;
    }
    const handle = window.requestAnimationFrame(() => {
      messagesListRef.current?.scrollToBottom();
    });
    return () => window.cancelAnimationFrame(handle);
  }, [scrollToBottomToken]);

  const fullCover = (zIndex: number): CSSProperties => ({
    position: 'absolute',
    inset: 0,
    zIndex,
  });
  const centeredCover = (zIndex: number, bg?: string): CSSProperties => ({
    ...fullCover(zIndex),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: bg,
  });
  const bottomPinned = (
    bottom: number | string,
    zIndex: number,
  ): CSSProperties => ({
    position: 'absolute',
    left: 0,
    right: 0,
    bottom,
    zIndex,
    pointerEvents: 'none',
  });
  // Top-edge overlay used by the pagination loader. `pointerEvents: 'none'`
  // so the underlying message list stays interactive — the loader is
  // feedback-only, no user input lands on it.
  const topPinned = (
    top: number | string,
    zIndex: number,
  ): CSSProperties => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top,
    zIndex,
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
  });

  return (
    <>
      {/* Messages list — flows in the document (window-scrolled). It owns no
          scroll container; horizontal padding only (see `ChatMessagesList`).
          Wrapped in a flow container whose `minHeight` reserves the inter-bar
          viewport band (between the sticky top chrome and the sticky composer)
          so the in-flow sticky composer stays bottom-pinned even when the list
          collapses to ~0 height — i.e. the empty conversation. Web analog of
          Flutter's expanding `messagesList()` slot in the chat `Column`
          (`chat_screen.dart:961-984`): there the messages slot fills, here the
          slab is given a minimum equal to the visible band. Once
          `messages.length > 0` the `VirtualList`'s reversed bottom-pin padding
          already meets or exceeds this band, so the `minHeight` is a no-op
          (content height ≥ minHeight). Uses the measured insets — no hard-coded
          chrome heights; `100vh` is the viewport-band reference (the page root
          already uses `minHeight: 100vh`), and the subtracted insets are
          measured. During the first render(s) before the insets attach they are
          `0`, so this over-reserves by at most the bar heights for one frame
          then settles — it never under-reserves. */}
      <div
        style={{
          minHeight: `calc(100vh - ${topInset}px - ${bottomChromeInset}px)`,
        }}
      >
        <ChatMessagesList
          ref={messagesListRef}
          qa={qa}
          messages={messages}
          peerUser={peerUser}
          currentUserId={currentUserId}
          openTimestampForMessageId={openTimestampForMessageId}
          onToggleTimestamp={onToggleTimestamp}
          onRetry={onRetry}
          onVideoCallJoin={onVideoCallJoin}
          onLoadMore={onLoadMore}
          onScrolledToBottom={onScrolledToBottom}
          onScrolledAway={onScrolledAway}
          myLastReadPosition={myLastReadPosition}
          screenKey={screenKey}
          reversedBottomPinInset={{ top: topInset, bottom: bottomChromeInset }}
        />
      </div>

      {/* Fixed overlay layer — spans the visible inter-bar band between the
          sticky app bar (top inset) and the sticky composer (bottom inset,
          plus the keyboard inset when the iOS keyboard raises the composer —
          Finding 4). Every overlay below is `position: absolute` against THIS
          layer (not the growing list), so they stay glued to the viewport
          while the document scrolls underneath. `pointerEvents: 'none'` on the
          layer so taps fall through to the list by default; interactive
          overlays (subscribe CTA, unread banner) re-enable it on their own
          child. Constrained to the page content column via `left`/`right`
          matching the page root (full width today; tighten here if a max-width
          lands). */}
      <div
        style={{
          position: 'fixed',
          top: topInset,
          bottom: bottomChromeInset,
          left: 0,
          right: 0,
          pointerEvents: 'none',
          zIndex: 40,
        }}
      >
        {/* Subscribe CTA — chat_screen.dart:3783-3830. Blurb is interpolated
            here (peer-name substitution) and passed pre-rendered, since
            `SubscribeCTA` is now l10n-agnostic for reuse across features.
            Interactive → re-enable pointer events on the child. */}
        {!isSubscribedMutual && peerUser !== null && (
          <div style={{ ...fullCover(2), pointerEvents: 'auto' }}>
            <SubscribeCTA
              blurb={l10n.chat.subscribeBlurb.replace('{peerName}', peerName)}
              buttonText={l10n.chat.subscribeButton}
              onSubscribePress={onSubscribePress}
            />
          </div>
        )}

        {/* Empty-state placeholder — chat_screen.dart:986-1008. */}
        {showEmptyState && (
          <div
            {...qaAttr({ id: 'chat-empty-encrypted' })}
            style={{ ...centeredCover(1), padding: responsive.spacing.lg }}
          >
            <span
              style={{
                color: theme.text.secondary,
                fontSize: responsive.font.lg,
                fontWeight: responsive.fontWeight.regular,
                textAlign: 'center',
                maxWidth: '80%',
              }}
            >
              {l10n.chat.emptyEncrypted.replace('{peerFirstName}', peerFirstName)}
            </span>
          </div>
        )}

        {/* Full-screen blocked overlay — chat_screen.dart:1010-1113.
            Interactive (its own buttons) → re-enable pointer events. */}
        {blockedVariant !== null && (
          <div style={{ ...fullCover(3), pointerEvents: 'auto' }}>
            <BlockedOverlays variant={blockedVariant} peerName={peerName} />
          </div>
        )}

        {/* Unread banner — chat_screen.dart:3380-3459. `onScrollDown` is wired
            to `handleScrollDown` which (1) imperatively snaps the list to the
            bottom via the `ChatMessagesList` handle and (2) calls the hook's
            `onScrolledToBottom` to flush the unread bucket / mark-seen.
            Interactive (tap-to-scroll) → re-enable pointer events. */}
        <div style={{ ...bottomPinned(responsive.spacing.lg, 4), pointerEvents: 'auto' }}>
          <UnreadBanner
            unreadPreview={unreadPreview}
            unreadBannerVisible={unreadBannerVisible}
            unreadCount={unreadCount}
            isScrolledUp={isScrolledUp}
            onScrollDown={handleScrollDown}
          />
        </div>

        {/* Send-error banner — chat_screen.dart:3461-3522. Positioned above
            the unread banner so they don't collide. */}
        {sendError && isOnline && (
          <div style={bottomPinned(responsive.spacing['3xl'], 5)}>
            <SendErrorBanner onDismiss={onSendErrorDismiss} />
          </div>
        )}

        {/* First-load indicator — chat_screen.dart:1287-1303. */}
        {isFirstLoad && (
          <div style={centeredCover(6, theme.background)}>
            <ExpauseLoadingIndicator />
          </div>
        )}

        {/* Pagination loader — Flutter parity: chat_screen.dart:3875-3884
            renders an `ExpauseLoadingIndicator` at the top of the reversed
            `ListView` while a page is in-flight. Web port pins the loader as
            an absolute overlay (top edge of the messages band) so it does
            not perturb the `VirtualList(reversed)` scroll math; see this
            file's header for the rationale. Suppressed during the first-load
            spinner, which already covers the region. */}
        {isFetchingMore && !isFirstLoad && (
          <div style={topPinned(responsive.spacing.sm, 7)}>
            <ExpauseLoadingIndicator />
          </div>
        )}
      </div>
    </>
  );
};
